// ---------------------------------------------------------------------------
// YouAreYou (uru) — Push Notifications + Quick Actions
//
// Handles all APNs push notification concerns for the iOS native app:
//   1. Permission request + device token registration with the backend
//   2. Notification action category registration (Connection + Sharing Quick Actions)
//   3. Background Quick Action handling (accept/decline without opening app)
//   4. Foreground notification display (in-app toast via callback)
//   5. Notification-tap deep link (open app to the relevant page)
//
// All code is gated on isNative — this module is a no-op on web.
//
// Backend endpoints required (flagged for Ian):
//   POST /api/device/register  { token: string, platform: "ios" }
//   POST /api/uru/connections/{id}  { action: "accept" | "deny" }  (existing)
//   POST /api/uru/connections/{id}  { action: "sharing_grant" | "sharing_revoke" }  (existing)
// ---------------------------------------------------------------------------

import {
  PushNotifications,
  type Token,
  type ActionPerformed,
  type PushNotificationSchema,
  // Note: notification action categories (CONNECTION_REQUEST, SHARING_REQUEST) are
  // registered natively in Xcode (AppDelegate.swift) for Capacitor v5+.
  // The JS layer only receives pushNotificationActionPerformed events.
} from '@capacitor/push-notifications'
import { isNative } from '@/lib/platform'
import { apiClient, isTokenExpired } from '@/api/client'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Shape of the APNs push payload data field we expect from Ian's backend. */
interface PushPayload {
  connection_id?: string | number
  request_id?: string | number
  category?: string
}

/** Callback invoked when a notification arrives while the app is in the foreground. */
export type ForegroundNotificationHandler = (notification: PushNotificationSchema) => void

/** Callback for navigating the app (used when a tap requires opening a page). */
export type NavigateFn = (path: string) => void

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let registered = false
let onForegroundNotification: ForegroundNotificationHandler | null = null
let navigateFn: NavigateFn | null = null

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Register the APNs device token with our backend so Ian's server can send
 * pushes to this device.
 */
async function registerTokenWithBackend(token: string): Promise<void> {
  try {
    await apiClient.post('/api/device/register', { token, platform: 'ios' })
  } catch {
    // Non-fatal — token registration will be retried on next login
  }
}

/**
 * Handle a Quick Action performed on a notification (user tapped Accept/Decline
 * directly on the notification without opening the app).
 *
 * The app is launched in the background. We fire the API call silently and
 * do NOT navigate — the user stays wherever they were.
 */
async function handleQuickAction(event: ActionPerformed): Promise<void> {
  const actionId = event.actionId
  const data = (event.notification.data ?? {}) as PushPayload
  const connectionId = data.connection_id ?? null
  const requestId = data.request_id ?? null

  // If the JWT is expired we can't make authenticated calls in the background.
  // Fall back to navigating to /requests so the user can act manually.
  if (isTokenExpired()) {
    navigateFn?.('/requests')
    return
  }

  try {
    if (
      (actionId === 'accept' || actionId === 'decline') &&
      connectionId != null
    ) {
      const action = actionId === 'accept' ? 'accept' : 'deny'
      await apiClient.post(`/api/uru/connections/${connectionId}`, { action })
    } else if (
      (actionId === 'grant' || actionId === 'decline') &&
      (requestId != null || connectionId != null)
    ) {
      const id = requestId ?? connectionId
      const action = actionId === 'grant' ? 'sharing_grant' : 'sharing_revoke'
      await apiClient.post(`/api/uru/connections/${id}`, { action })
    }
    // actionId === 'tap' (user tapped notification body, no action button) is
    // handled by the regular tap handler below.
  } catch {
    // Background call failed — not recoverable silently. The user will see the
    // pending request when they next open the app.
  }
}

/**
 * Handle a notification tap (user tapped the notification body to open the app).
 */
function handleNotificationTap(event: ActionPerformed): void {
  const data = (event.notification.data ?? {}) as PushPayload
  const connectionId = data.connection_id

  if (connectionId != null) {
    navigateFn?.(`/connections/${connectionId}`)
  } else {
    navigateFn?.('/requests')
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Register for push notifications and set up all event handlers.
 *
 * Call once after successful login, from AuthContext. Safe to call multiple
 * times — subsequent calls are no-ops.
 *
 * @param onForeground  Called when a notification arrives while app is open.
 * @param navigate      React Router navigate function for deep linking.
 */
export async function initPushNotifications(
  onForeground: ForegroundNotificationHandler,
  navigate: NavigateFn,
): Promise<void> {
  if (!isNative || registered) return
  registered = true

  onForegroundNotification = onForeground
  navigateFn = navigate

  // 1. Request permission
  const { receive } = await PushNotifications.requestPermissions()
  if (receive !== 'granted') return

  // 2. Register with APNs
  await PushNotifications.register()

  // 3. APNs token received — send to backend
  // Note: notification action categories (CONNECTION_REQUEST, SHARING_REQUEST) must be
  // registered natively in Xcode AppDelegate.swift — registerActionTypes was removed
  // from @capacitor/push-notifications in v5+.
  //
  // Add to AppDelegate.swift:
  //   UNUserNotificationCenter.current().setNotificationCategories([
  //     UNNotificationCategory(identifier: "CONNECTION_REQUEST", actions: [
  //       UNNotificationAction(identifier: "accept",  title: "Accept",  options: []),
  //       UNNotificationAction(identifier: "decline", title: "Decline", options: [.destructive]),
  //     ], intentIdentifiers: [], options: []),
  //     UNNotificationCategory(identifier: "SHARING_REQUEST", actions: [
  //       UNNotificationAction(identifier: "grant",   title: "Grant",   options: []),
  //       UNNotificationAction(identifier: "decline", title: "Decline", options: [.destructive]),
  //     ], intentIdentifiers: [], options: []),
  //   ])
  PushNotifications.addListener('registration', (token: Token) => {
    void registerTokenWithBackend(token.value)
  })

  // 5. Foreground notification received — show in-app toast
  PushNotifications.addListener(
    'pushNotificationReceived',
    (notification: PushNotificationSchema) => {
      onForegroundNotification?.(notification)
    },
  )

  // 6. Notification action performed (Quick Action button OR notification tap)
  PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (event: ActionPerformed) => {
      if (event.actionId && event.actionId !== 'tap') {
        // User tapped a Quick Action button (Accept / Decline / Grant)
        void handleQuickAction(event)
      } else {
        // User tapped the notification body — deep link into the app
        handleNotificationTap(event)
      }
    },
  )
}
